import { DocumentAttemptStage, DocumentAttemptWhere, DocumentDeviceJobRecord, DocumentJobAttempt, DocumentJobStatus } from '@numeo/types-core'

/** Plain words for where a job is now. The internal name stays in the tooltip, for whoever needs it. */
export const STATUS_LABEL: Record<DocumentJobStatus, string> = {
    [DocumentJobStatus.UPLOAD_RUNNING]: 'A user is waiting',
    [DocumentJobStatus.DUPLICATE_WAITING]: 'Waiting on a duplicate',
    [DocumentJobStatus.OCR_QUEUED]: 'Waiting for OCR',
    [DocumentJobStatus.OCR_LEASED]: 'Being read now',
    [DocumentJobStatus.OCR_DONE]: 'Read, waiting for the LLM',
    [DocumentJobStatus.OCR_NO_TEXT]: 'No text, the LLM reads the images',
    [DocumentJobStatus.OCR_FAILED]: 'Could not be read',
    [DocumentJobStatus.LLM_LEASED]: 'With the LLM',
    [DocumentJobStatus.LLM_DONE]: 'Done',
    [DocumentJobStatus.LLM_NOT_OF_INTEREST]: 'Not a document we need',
    [DocumentJobStatus.LLM_COMBINED]: 'Holds several documents, not read',
    [DocumentJobStatus.LLM_FAILED]: 'The LLM failed',
}

/** `working` is a job being read right now; `unrecorded` is a job with no OCR try of this device on record. */
type OutcomeTone = 'success' | 'warning' | 'working' | 'unrecorded'

/** What this device did with a job: a sentence, and the reason when it did not deliver. */
interface DeviceOutcome {
    tone: OutcomeTone
    text: string
    detail: string | null
}

/** This device's OCR tries at the job: the Mac's own when the attempts name it, otherwise every local one. */
function deviceAttempts(job: DocumentDeviceJobRecord, deviceNames: string[]): DocumentJobAttempt[] {
    const local = job.attempts.filter((a) => a.stage === DocumentAttemptStage.OCR && a.where === DocumentAttemptWhere.LOCAL)
    const named = local.filter((a) => a.device !== null && deviceNames.includes(a.device))
    return named.length ? named : local
}

/** What the device did with the job, from its last try. */
export function deviceOutcome(job: DocumentDeviceJobRecord, deviceNames: string[]): DeviceOutcome {
    const mine = deviceAttempts(job, deviceNames)
    const last = mine[mine.length - 1]
    if (!last) {
        return job.status === DocumentJobStatus.OCR_LEASED
            ? { tone: 'working', text: 'Reading it now', detail: null }
            : { tone: 'unrecorded', text: 'No result recorded', detail: null }
    }
    if (last.ok) return { tone: 'success', text: `Read it with ${last.reader}`, detail: null }
    return { tone: 'warning', text: `${last.reader} did not deliver`, detail: last.reason ?? 'no reason given' }
}

/** The tries by other workers, and this device's earlier ones: everything but its last try. */
export function otherTries(job: DocumentDeviceJobRecord, deviceNames: string[]): DocumentJobAttempt[] {
    const mine = deviceAttempts(job, deviceNames)
    const last = mine[mine.length - 1]
    return last ? job.attempts.filter((a) => a !== last) : job.attempts
}

interface DeviceJobSummary {
    total: number
    read: number
    notDelivered: number
    inProgress: number
    unrecorded: number
}

export function summarize(jobs: DocumentDeviceJobRecord[], deviceNames: string[]): DeviceJobSummary {
    const summary: DeviceJobSummary = { total: jobs.length, read: 0, notDelivered: 0, inProgress: 0, unrecorded: 0 }
    for (const job of jobs) {
        const outcome = deviceOutcome(job, deviceNames)
        if (outcome.tone === 'success') summary.read++
        else if (outcome.tone === 'warning') summary.notDelivered++
        else if (outcome.tone === 'working') summary.inProgress++
        else summary.unrecorded++
    }
    return summary
}

/** A job worth a look: this device did not deliver, or the job ended in a failure. */
export function isProblem(job: DocumentDeviceJobRecord, deviceNames: string[]): boolean {
    return deviceOutcome(job, deviceNames).tone === 'warning' || job.status === DocumentJobStatus.OCR_FAILED || job.status === DocumentJobStatus.LLM_FAILED
}
